import { useEffect, useState } from 'react';
import type { ClasseDesvio, FaixaOrcado, StatusLote } from '@/types/dashboards';

/**
 * Cores dos painéis executivos.
 *
 * Não reaproveita a `palette` do `useThemeColors` (amarelo/ciano da marca): ela
 * não passa na checagem de daltonismo nem na faixa de luminosidade para séries.
 * As oito cores abaixo são a paleta categórica validada (script do dataviz) contra
 * as superfícies reais do h.lab — `--bg-card` #002538 no escuro e #FFFFFF no claro.
 * No claro, aqua/amarelo/magenta ficam abaixo de 3:1: por isso toda série tem
 * rótulo ou tabela ao lado (regra de alívio), nunca só a cor.
 *
 * A cor segue a entidade, não a posição: "Produzido" é sempre laranja, "100 ML"
 * é sempre azul, com qualquer filtro.
 */

export type ModoTema = 'light' | 'dark';

/**
 * Medidas dos gráficos (26/10/2026, pedido do Marcelo: "o pessoal ver melhor"). Um gráfico por
 * linha, mais alto, e o texto do eixo e dos rótulos em 14 px (era 10–11; 12 em 26/10, 14 desde
 * 03/11/2026, junto com a escala de fonte da plataforma).
 */
export const FONTE_EIXO = 14;
export const FONTE_ROTULO = 14;
/** Gráfico principal, ocupando a largura toda. */
export const ALTURA_GRAFICO = 380;
/** Gráfico que se repete por linha ou por máquina (daily, linha do tempo). */
export const ALTURA_GRAFICO_REPETIDO = 220;
/** Altura de cada barra nos gráficos de barra horizontal (uma linha por item). */
export const ALTURA_BARRA_HORIZONTAL = 36;

const CATEGORICA: Record<ModoTema, string[]> = {
  light: ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948'],
  dark: ['#3987e5', '#d95926', '#199e70', '#c98500', '#d55181', '#008300', '#9085e9', '#e66767'],
};

/** Status é fixo nos dois temas e sempre acompanha ícone + rótulo. */
export const STATUS = {
  bom: '#0ca30c',
  atencao: '#fab219',
  serio: '#ec835a',
  critico: '#d03b3b',
} as const;

// O cinza também vira texto (status "Futuro" na tabela): no claro, #898781 dava 3,6:1 no branco.
const NEUTRO: Record<ModoTema, string> = { light: '#6D6C67', dark: '#8899AA' };

/** Cinza neutro do tema: o que não é bom nem ruim (futuro, fora da conta). */
export const corNeutra = (modo: ModoTema): string => NEUTRO[modo];

export interface VisualFaixa {
  rotulo: string;
  icone: string;
  cor: string;
}

export function visualFaixa(classe: ClasseDesvio, modo: ModoTema): VisualFaixa {
  switch (classe) {
    case 'critico':
      return { rotulo: 'Crítico', icone: 'error', cor: STATUS.critico };
    case 'alerta':
      return { rotulo: 'Alerta', icone: 'warning', cor: STATUS.atencao };
    case 'normal':
      return { rotulo: 'No plano', icone: 'check_circle', cor: STATUS.bom };
    case 'futuro':
      return { rotulo: 'Futuro', icone: 'schedule', cor: NEUTRO[modo] };
    default:
      return { rotulo: 'Sem OP no mês', icone: 'remove_circle_outline', cor: NEUTRO[modo] };
  }
}

/** Desvio contra o orçado (DRE): o contábil gasta mais ou menos que o BGC previu. */
export function visualFaixaOrcado(faixa: FaixaOrcado): VisualFaixa {
  switch (faixa) {
    case 'critico':
      return { rotulo: 'Acima do orçado', icone: 'error', cor: STATUS.critico };
    case 'atencao':
      return { rotulo: 'Atenção', icone: 'warning', cor: STATUS.atencao };
    case 'melhor':
      return { rotulo: 'Abaixo do orçado', icone: 'savings', cor: STATUS.bom };
    default:
      return { rotulo: 'No orçado', icone: 'check_circle', cor: STATUS.bom };
  }
}

/** Status de um lote no CQ — sempre com ícone e rótulo. */
export function visualStatusLote(status: StatusLote, modo: ModoTema): VisualFaixa {
  switch (status) {
    case 'liberada':
      return { rotulo: 'Liberado', icone: 'check_circle', cor: STATUS.bom };
    case 'pendente':
      return { rotulo: 'Aguardando CQ', icone: 'hourglass_top', cor: STATUS.atencao };
    case 'rejeicao_pendente':
      return { rotulo: 'Rejeição sem destino', icone: 'report', cor: STATUS.serio };
    case 'cancelada':
      return { rotulo: 'Cancelado', icone: 'block', cor: NEUTRO[modo] };
    case 'sem_saldo':
      return { rotulo: 'Ficha aberta sem saldo no CQ', icone: 'inventory', cor: NEUTRO[modo] };
    default:
      return { rotulo: 'Outro', icone: 'help', cor: NEUTRO[modo] };
  }
}

/**
 * Ordem canônica das categorias de volume: define a cor de cada uma. As duas que
 * o painel abre selecionadas (100 e 250 ML) ficam com os dois primeiros slots.
 */
const ORDEM_CATEGORIAS = ['100 ML', '250 ML', '500 ML', '1000 ML', '10 ML', '5 ML', '50 ML', 'Outros'];

export function corCategoria(categoria: string, todas: string[], modo: ModoTema): string {
  const extras = todas.filter((c) => !ORDEM_CATEGORIAS.includes(c));
  const ordem = [...ORDEM_CATEGORIAS, ...extras];
  const idx = ordem.indexOf(categoria);
  // Nunca gera nona cor: além do oitavo slot a série fica neutra.
  return idx >= 0 && idx < 8 ? CATEGORICA[modo][idx] : NEUTRO[modo];
}

/** Série de índice fixo na paleta categórica (a cor segue a série, nunca a posição na tela). */
export function corSerie(indice: number, modo: ModoTema): string {
  return indice >= 0 && indice < 8 ? CATEGORICA[modo][indice] : NEUTRO[modo];
}

export interface CoresPainel {
  modo: ModoTema;
  programado: string;
  produzido: string;
  faturado: string;
  acima: string;
  abaixo: string;
  neutro: string;
  texto: string;
  textoMuted: string;
  grade: string;
  eixo: string;
}

/** Texto do eixo: cor apagada do tema, no tamanho do `FONTE_EIXO`. */
export const tickEixo = (cores: Pick<CoresPainel, 'textoMuted'>) => ({ fill: cores.textoMuted, fontSize: FONTE_EIXO });

function lerModo(): ModoTema {
  if (typeof document === 'undefined') return 'dark';
  return document.documentElement.getAttribute('data-theme') === 'light' ? 'light' : 'dark';
}

function montar(modo: ModoTema): CoresPainel {
  const cat = CATEGORICA[modo];
  return {
    modo,
    programado: cat[0],
    produzido: cat[1],
    faturado: cat[2],
    // Divergente azul ↔ vermelho: direção do desvio, não "bom/ruim".
    acima: cat[0],
    abaixo: cat[7],
    neutro: NEUTRO[modo],
    texto: modo === 'light' ? '#003046' : '#F1F5F9',
    // Os mesmos `--text-muted` do index.css (03/11/2026): ≥ 4,5:1 no card nos dois temas.
    textoMuted: modo === 'light' ? '#4C6878' : '#879CAB',
    grade: modo === 'light' ? '#E5E1DA' : '#003550',
    eixo: modo === 'light' ? '#C9C4BA' : '#2A5F7A',
  };
}

/**
 * Observa `data-theme` no <html> (mesmo motivo do `useThemeColors`: o "Exportar
 * PDF" troca o tema direto no DOM) e devolve as cores do tema corrente.
 */
export function useCoresPainel(): CoresPainel {
  const [modo, setModo] = useState<ModoTema>(lerModo);

  useEffect(() => {
    const html = document.documentElement;
    const observer = new MutationObserver(() => setModo(lerModo()));
    observer.observe(html, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  return montar(modo);
}
